import { ComponentVerticalPlacementType } from "building-types/components/componentInstanceTypes";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { AbsoluteVerticalDatumEntity, DatumEntity } from "building-types/datums/datumEntityTypes";
import { isAbsoluteVerticalDatum } from "building-types/datums/datumEntityTypes";
import type { RWTable, Table } from "common/data/table";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { DIST_EPSILON } from "geom/core";
import { createDatumAbstraction } from "../../elements/datums/datumAbstractionFactory";

export function createDatumAbstractionComponentInstances(
  datumId: string,
  datum: DatumEntity,
  componentToTransforms: Table<readonly ComponentTransform[]>,
  datumAbstractions: RWTable<DatumAbstraction>,
): void {
  // skip non-absolute vertical datums
  if (!isAbsoluteVerticalDatum(datum)) {
    return;
  }

  for (const componentId in componentToTransforms) {
    const transforms = componentToTransforms[componentId];
    for (const transform of transforms) {
      const { componentInstanceChain, verticalPlacement } = transform;
      const { type } = verticalPlacement;

      // only replication vertical placement creates ephemeral datum abstraction copies,
      // because all the levels are created as ephemeral levels and moved as a group
      if (type !== ComponentVerticalPlacementType.REPLICATION) {
        continue;
      }

      const { offset } = verticalPlacement;

      // no need to create datum abstraction copy when the vertical offset is 0
      if (Math.abs(offset) < DIST_EPSILON) {
        continue;
      }

      const instanceDatumId = instanceIdForComponent(datumId, componentInstanceChain);

      const datumInstance: AbsoluteVerticalDatumEntity = {
        ...datum,
        id: instanceDatumId,
        offset: (datum.offset ?? 0) + offset,
      };

      datumAbstractions[instanceDatumId] = createDatumAbstraction(datumInstance);
    }
  }
}
